import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaPhoneAlt, FaWhatsapp } from "react-icons/fa";

export default function PhoneLink({
    phone,
    C,
    dark,
    className = "",
    style = {},
    icon,
    iconOnly = false,
    children,
    onMouseEnter,
    onMouseLeave,
}) {
    const [isDesktop, setIsDesktop] = useState(true);
    const [open, setOpen] = useState(false);

    const wrapRef = useRef(null);
    const menuRef = useRef(null);

    const [position, setPosition] = useState({
        top: 0,
        left: 0,
        width: 224,
    });

    // Safely handle null / undefined phone
    const safePhone = phone ? String(phone).trim() : "";

    const cleanPhone = safePhone
        ? safePhone.replace(/[^+\d]/g, "")
        : "";

    // -----------------------------------------
    // HOOK 1
    // -----------------------------------------
    useEffect(() => {
        const check = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };

        check();

        window.addEventListener("resize", check);

        return () => {
            window.removeEventListener("resize", check);
        };
    }, []);

    // -----------------------------------------
    // POSITION
    // -----------------------------------------
    const updatePosition = () => {
        if (!wrapRef.current) return;

        const rect = wrapRef.current.getBoundingClientRect();

        let left = rect.left;

        if (left + 224 > window.innerWidth - 16) {
            left = window.innerWidth - 224 - 16;
        }

        setPosition({
            top: rect.bottom + window.scrollY + 8,
            left,
            width: 224,
        });
    };

    // -----------------------------------------
    // HOOK 2
    // -----------------------------------------
    useEffect(() => {
        if (!open) return;

        updatePosition();

        const handleResize = () => {
            updatePosition();
        };

        const handleClickOutside = (e) => {
            if (
                wrapRef.current &&
                !wrapRef.current.contains(e.target) &&
                menuRef.current &&
                !menuRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        window.addEventListener("scroll", handleResize, true);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("scroll", handleResize, true);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open]);

    // -----------------------------------------
    // NOW it is safe to return
    // -----------------------------------------
    if (!safePhone) {
        return null;
    }

    // -----------------------------------------
    // PROVIDERS
    // -----------------------------------------
    const providers = [
        {
            id: "call",
            name: "Call",
            icon: <FaPhoneAlt color="#1877F2" />,
            href: `tel:${cleanPhone}`,
        },
        {
            id: "whatsapp",
            name: "WhatsApp",
            icon: <FaWhatsapp color="#25D366" />,
            href: `https://wa.me/${cleanPhone.replace("+", "")}`,
        },
    ];

    return (
        <>
            <span
                ref={wrapRef}
                className="inline-block"
            >
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    className={`inline-flex items-center gap-2 transition ${className}`}
                    style={style}
                >
                    {children || icon}

                </button>
            </span>

            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        style={{
                            position: "absolute",
                            top: position.top,
                            left: position.left,
                            width: position.width,
                            background: C?.panel || "#111827",
                            border: `1px solid ${C?.line || "#374151"}`,
                            borderRadius: 16,
                            overflow: "hidden",
                            zIndex: 999999,
                            boxShadow: "0 18px 45px rgba(0,0,0,.25)",
                        }}
                    >
                        {isDesktop ? (
                            providers.map((p) => (
                                <a
                                    key={p.id}
                                    href={p.href}
                                    target={
                                        p.id === "whatsapp"
                                            ? "_blank"
                                            : "_self"
                                    }
                                    rel="noopener noreferrer"
                                    onClick={() => setOpen(false)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        padding: "14px 18px",
                                        color: C?.text || "#fff",
                                        textDecoration: "none",
                                        transition: ".25s",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background =
                                            dark
                                                ? "rgba(255,255,255,.05)"
                                                : "rgba(0,0,0,.05)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background =
                                            "transparent";
                                    }}
                                >
                                    {p.icon}
                                    {p.name}
                                </a>
                            ))
                        ) : (
                            <>
                                {/* Call */}
                                <a
                                    href={`tel:${cleanPhone}`}
                                    onClick={() => setOpen(false)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        padding: "14px 18px",
                                        color: C?.text || "#fff",
                                        textDecoration: "none",
                                    }}
                                >
                                    <FaPhoneAlt color="#1877F2" />
                                    Call
                                </a>

                                {/* WhatsApp */}
                                <a
                                    href={`https://wa.me/${cleanPhone.replace("+", "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setOpen(false)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        padding: "14px 18px",
                                        color: C?.text || "#fff",
                                        textDecoration: "none",
                                    }}
                                >
                                    <FaWhatsapp color="#25D366" />
                                    WhatsApp
                                </a>
                            </>
                        )}
                    </div>,
                    document.body
                )}
        </>
    );
}